/**
 * Gerüst und Seitenkopf.
 *
 * Die Navigation ist nach Arbeitsabläufen gruppiert, nicht nach Tabellen:
 * Was täglich passiert (Belege, Rechnungen) steht oben, die Auswertung in
 * der Mitte, die Verwaltung unten — dort wird selten etwas geändert.
 */
import type { ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useSitzung } from '../sitzung/SitzungKontext';

type Eintrag = { pfad: string; text: string; zeichen: string };

const TAEGLICH: Eintrag[] = [
  { pfad: '/', text: 'Übersicht', zeichen: '◧' },
  { pfad: '/belege', text: 'Belege', zeichen: '▤' },
  { pfad: '/rechnungen', text: 'Rechnungen', zeichen: '€' },
  { pfad: '/angebote', text: 'Angebote', zeichen: '✎' },
  { pfad: '/bank', text: 'Bank', zeichen: '⇄' },
];

const AUSWERTEN: Eintrag[] = [
  { pfad: '/auswertung', text: 'EÜR', zeichen: '∑' },
  { pfad: '/kontakte', text: 'Kontakte', zeichen: '☺' },
];

const VERWALTUNG: Eintrag[] = [
  { pfad: '/stammdaten', text: 'Stammdaten', zeichen: '⚙' },
  { pfad: '/konto', text: 'Konto', zeichen: '⚿' },
];

function Gruppe({ titel, eintraege }: { titel: string; eintraege: Eintrag[] }): ReactNode {
  return (
    <div className="nav-gruppe">
      <h4>{titel}</h4>
      {eintraege.map((e) => (
        <NavLink
          key={e.pfad}
          to={e.pfad}
          end={e.pfad === '/'}
          className={({ isActive }) => `nav-eintrag${isActive ? ' aktiv' : ''}`}
        >
          <span className="nav-zeichen" aria-hidden="true">
            {e.zeichen}
          </span>
          {e.text}
        </NavLink>
      ))}
    </div>
  );
}

export function Geruest(): ReactNode {
  const { benutzer, abmelden } = useSitzung();

  return (
    <div className="geruest">
      <nav className="seitenleiste">
        <div className="marke">
          <strong>GreenJar</strong>
          <span>Buchhaltung</span>
        </div>

        <div className="navigation">
          <Gruppe titel="Laufend" eintraege={TAEGLICH} />
          <Gruppe titel="Auswerten" eintraege={AUSWERTEN} />
          <Gruppe titel="Verwaltung" eintraege={VERWALTUNG} />
        </div>

        <div className="seitenleiste-fuss">
          <strong>{benutzer?.name}</strong>
          <span className="klein">
            {benutzer?.rolle === 'lesen'
              ? 'Nur Lesezugriff'
              : benutzer?.rolle === 'steuerberater'
                ? 'Steuerberater'
                : 'Inhaber'}
          </span>
          <button
            type="button"
            className="knopf schlicht klein"
            style={{ marginTop: '0.375rem', marginInlineStart: '-0.375rem' }}
            onClick={() => void abmelden()}
          >
            Abmelden
          </button>
        </div>
      </nav>

      <main className="inhalt">
        <Outlet />
      </main>
    </div>
  );
}

/** Kopf und Körper einer Seite. */
export function Seite({
  titel,
  untertitel,
  aktionen,
  children,
}: {
  titel: string;
  /* `| undefined` ausdrücklich: Mit exactOptionalPropertyTypes ist ein
     weggelassenes Feld nicht dasselbe wie ein ausdrücklich undefiniertes.
     Für Anzeigefelder, die aus einem noch nicht geladenen Wert kommen, ist
     der Unterschied ohne Belang — für Daten, die an die API gehen, sehr
     wohl. Deshalb nur hier. */
  untertitel?: string | undefined;
  aktionen?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <header className="inhalt-kopf">
        <div className="titelzeile">
          <h1>{titel}</h1>
          {untertitel ? <span className="untertitel">{untertitel}</span> : null}
        </div>
        {aktionen ? <div className="reihe">{aktionen}</div> : null}
      </header>
      <div className="inhalt-koerper">{children}</div>
    </>
  );
}
